import { cn } from "@/utils";
import {
  addDays,
  addMonths,
  endOfMonth,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Tooltip } from "./Tooltip";

/**
 * Calendário de mês da Fidexa.
 *
 * Substitui o `<input type="date">` nativo pelo mesmo motivo do `Select`: o
 * nativo abre a roda do iOS, o painel do Android e um calendário diferente em
 * cada navegador de desktop — quatro aparências para o mesmo campo, nenhuma
 * delas a Fidexa. Pior: no Chrome ele mostra `dd/mm/yyyy` como se fosse
 * instrução de digitação, e o lojista tenta escrever a data ali.
 *
 * O que o nativo dava de graça e aqui está implementado: teclado completo
 * (setas, PageUp/PageDown, Home/End, Enter/Espaço), `role="grid"` com o dia
 * marcado por `aria-selected`, hoje por `aria-current="date"`, e alvo de toque
 * de 44px em cada dia.
 *
 * `rangeStart`/`rangeEnd` pintam os dias entre duas datas. Servem ao período
 * montado em dois campos ("De" e "Até"): sem esse rastro, o usuário escolhe a
 * segunda data sem enxergar o intervalo que está formando.
 *
 * `isDateDisabled` bloqueia dias avulsos (ex.: dia em que a Fidexa não envia
 * mensagem) e `getDayTooltip` explica o bloqueio ao passar o mouse ou focar o
 * dia — o dia bloqueado usa `aria-disabled`, não `disabled`, então continua
 * recebendo eventos de ponteiro e o tooltip funciona nele. `disabled` trava a
 * grade inteira (ex.: enquanto os bloqueios carregam).
 *
 * O título do mês é um botão: abre a grade de anos e, escolhido o ano, a de
 * meses. Sem isso, chegar a uma data de nascimento custava centenas de cliques
 * na seta. As grades ficam dentro do mesmo painel (não é outro popover), então
 * o clique nelas não conta como "fora" e não fecha o `DateField`.
 */

export interface CalendarProps {
  value?: Date | null;
  onSelect: (date: Date) => void;
  /** Antes disso não dá para escolher. */
  min?: Date | null;
  /** Depois disso não dá para escolher. */
  max?: Date | null;
  /** Realce contínuo entre duas datas. */
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  /** Nome do grid para leitores de tela. */
  label?: string;
  /** Põe o foco no dia ativo ao montar — é o que o `DateField` faz ao abrir. */
  autoFocus?: boolean;
  /** Bloqueia dias avulsos, além de `min`/`max`. */
  isDateDisabled?: (date: Date) => boolean;
  /** Tooltip do dia (ex.: motivo do bloqueio); `null` = sem tooltip. */
  getDayTooltip?: (date: Date) => ReactNode | null;
  /** Nenhum dia selecionável (ex.: enquanto os bloqueios carregam). */
  disabled?: boolean;
  /** Avisa o primeiro dia do mês que passou a ser exibido. */
  onMonthChange?: (month: Date) => void;
  className?: string;
}

/** Sigla de cada dia, começando no domingo, como o calendário de parede. */
const DIAS_DA_SEMANA = Array.from({ length: 7 }, (_, indice) => {
  const dia = addDays(startOfWeek(new Date(), { locale: ptBR }), indice);
  return {
    curto: dia
      .toLocaleDateString("pt-BR", { weekday: "short" })
      .replace(".", ""),
    longo: dia.toLocaleDateString("pt-BR", { weekday: "long" }),
  };
});

const nomeDoMes = (mes: Date) =>
  mes.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

/**
 * Sempre seis semanas, completando com os dias do mês seguinte. Um mês ocupa
 * de 4 a 6 linhas; se a grade acompanhasse, a altura mudaria a cada troca de
 * mês e as setas fugiriam do dedo — quem volta vários meses acabava tocando
 * num dia ou fora do painel e fechando o calendário.
 */
const SEMANAS_NA_GRADE = 6;

/** Anos por página da grade de anos: 4 linhas de 3, como a de meses. */
const ANOS_POR_PAGINA = 12;

const MESES_DO_ANO = Array.from({ length: 12 }, (_, indice) =>
  new Date(2000, indice, 1)
    .toLocaleDateString("pt-BR", { month: "short" })
    .replace(".", "")
);

/** Primeiro ano da página que contém `ano` (páginas alinhadas em múltiplos de 12). */
const inicioDaPagina = (ano: number) =>
  ano - (((ano % ANOS_POR_PAGINA) + ANOS_POR_PAGINA) % ANOS_POR_PAGINA);

type Visao = "dias" | "meses" | "anos";

const CLASSE_DA_SETA =
  "rounded-ds-md text-ds-ink-700 hover:bg-ds-ink-100 hover:text-ds-ink-900 focus-visible:ring-ds-focus focus-visible:ring-offset-ds-surface inline-flex size-11 cursor-pointer items-center justify-center transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

const CLASSE_DA_OPCAO =
  "ds-numeric rounded-ds-md font-ds-sans text-ds-small ease-ds-out inline-flex h-11 w-full cursor-pointer items-center justify-center transition-colors duration-150 first-letter:uppercase focus-visible:ring-ds-focus focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-40";

const semanasDoMes = (mes: Date): Date[][] => {
  const inicio = startOfWeek(startOfMonth(mes), { locale: ptBR });

  return Array.from({ length: SEMANAS_NA_GRADE }, (_, semana) =>
    Array.from({ length: 7 }, (_, dia) => addDays(inicio, semana * 7 + dia))
  );
};

export const Calendar = ({
  value,
  onSelect,
  min,
  max,
  rangeStart,
  rangeEnd,
  label = "Calendário",
  autoFocus = false,
  isDateDisabled,
  getDayTooltip,
  disabled = false,
  onMonthChange,
  className,
}: CalendarProps) => {
  const hoje = startOfDay(new Date());
  const inicial = value ?? hoje;

  const [mesVisivel, setMesVisivelState] = useState(startOfMonth(inicial));
  const setMesVisivel = (mes: Date) => {
    setMesVisivelState(mes);
    onMonthChange?.(mes);
  };
  // O dia que recebe o foco do teclado. Um `tabIndex` por dia faria o Tab
  // percorrer 42 paradas antes de sair do calendário.
  const [focado, setFocado] = useState(startOfDay(inicial));
  const gradeRef = useRef<HTMLTableElement>(null);
  const moverFoco = useRef(autoFocus);
  const [visao, setVisao] = useState<Visao>("dias");
  // Ano cujos meses aparecem na visão "meses" e página da visão "anos".
  const [anoEscolhido, setAnoEscolhido] = useState(mesVisivel.getFullYear());
  const [paginaDeAnos, setPaginaDeAnos] = useState(
    inicioDaPagina(mesVisivel.getFullYear())
  );

  const bloqueado = (dia: Date) =>
    disabled ||
    (!!min && isBefore(dia, startOfDay(min))) ||
    (!!max && isAfter(dia, startOfDay(max))) ||
    (!!isDateDisabled && isDateDisabled(dia));

  // Depois de navegar pelo teclado, o foco acompanha o dia ativo — senão as
  // setas mudariam a seleção visual e o foco continuaria parado.
  useEffect(() => {
    if (!moverFoco.current) return;
    moverFoco.current = false;
    gradeRef.current
      ?.querySelector<HTMLButtonElement>('button[tabindex="0"]')
      ?.focus();
  });

  const irPara = (dia: Date) => {
    moverFoco.current = true;
    setFocado(dia);
    if (!isSameMonth(dia, mesVisivel)) setMesVisivel(startOfMonth(dia));
  };

  const aoTeclar = (evento: KeyboardEvent<HTMLTableElement>) => {
    const passos: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };

    if (evento.key in passos) {
      evento.preventDefault();
      irPara(addDays(focado, passos[evento.key]));
      return;
    }

    if (evento.key === "Home" || evento.key === "End") {
      evento.preventDefault();
      const semana = startOfWeek(focado, { locale: ptBR });
      irPara(evento.key === "Home" ? semana : addDays(semana, 6));
      return;
    }

    if (evento.key === "PageUp" || evento.key === "PageDown") {
      evento.preventDefault();
      irPara(addMonths(focado, evento.key === "PageUp" ? -1 : 1));
      return;
    }

    if (evento.key === "Enter" || evento.key === " ") {
      evento.preventDefault();
      if (!bloqueado(focado)) onSelect(focado);
    }
  };

  const trocarMes = (passo: number) => {
    const novo = addMonths(mesVisivel, passo);
    setMesVisivel(novo);
    // O dia focado acompanha o mês, senão a próxima seta do teclado saltaria de
    // volta para o mês anterior.
    setFocado(anterior => addMonths(anterior, passo));
  };

  // Ano ou mês inteiro fora de min/max não tem o que escolher.
  const anoForaDoLimite = (ano: number) =>
    (!!min && ano < min.getFullYear()) || (!!max && ano > max.getFullYear());
  const mesForaDoLimite = (ano: number, mes: number) => {
    const inicio = new Date(ano, mes, 1);
    return (
      (!!min && isBefore(endOfMonth(inicio), startOfDay(min))) ||
      (!!max && isAfter(inicio, startOfDay(max)))
    );
  };

  const abrirAnos = () => {
    setPaginaDeAnos(inicioDaPagina(mesVisivel.getFullYear()));
    setVisao("anos");
  };

  const escolherAno = (ano: number) => {
    setAnoEscolhido(ano);
    setVisao("meses");
  };

  const escolherMes = (mes: number) => {
    const novo = new Date(anoEscolhido, mes, 1);
    setMesVisivel(novo);
    // Mesmo dia no mês novo, cortado no último dia (31 → 30 de abril).
    setFocado(
      new Date(
        anoEscolhido,
        mes,
        Math.min(focado.getDate(), endOfMonth(novo).getDate())
      )
    );
    moverFoco.current = true;
    setVisao("dias");
  };

  const dentroDoIntervalo = (dia: Date) => {
    if (!rangeStart || !rangeEnd) return false;
    const inicio = startOfDay(rangeStart);
    const fim = startOfDay(rangeEnd);
    return !isBefore(dia, inicio) && !isAfter(dia, fim);
  };

  return (
    <div
      className={cn(
        "rounded-ds-lg bg-ds-surface flex w-full flex-col gap-2 p-3 sm:w-auto",
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() =>
            visao === "dias"
              ? trocarMes(-1)
              : visao === "meses"
                ? setAnoEscolhido(ano => ano - 1)
                : setPaginaDeAnos(inicio => inicio - ANOS_POR_PAGINA)
          }
          disabled={
            (visao === "meses" && anoForaDoLimite(anoEscolhido - 1)) ||
            (visao === "anos" && anoForaDoLimite(paginaDeAnos - 1))
          }
          aria-label={
            visao === "dias"
              ? "Mês anterior"
              : visao === "meses"
                ? "Ano anterior"
                : "Anos anteriores"
          }
          className={CLASSE_DA_SETA}
        >
          <ChevronLeft aria-hidden="true" className="size-5" />
        </button>

        {/* `aria-live`: quem navega por teclado troca de mês com PageUp e
            precisa ouvir para onde foi. O título abre a escolha do ano. */}
        {visao === "anos" ? (
          <span
            aria-live="polite"
            className="font-ds-sans text-ds-body text-ds-ink-900 ds-numeric font-semibold"
          >
            {paginaDeAnos} – {paginaDeAnos + ANOS_POR_PAGINA - 1}
          </span>
        ) : (
          <button
            type="button"
            onClick={abrirAnos}
            aria-label={`${
              visao === "dias" ? nomeDoMes(mesVisivel) : anoEscolhido
            }. Escolher o ano`}
            aria-live="polite"
            // `first-letter` e não `capitalize`: o segundo põe maiúscula em
            // toda palavra e o mês vira "Setembro De 2026".
            className="rounded-ds-md font-ds-sans text-ds-body text-ds-ink-900 hover:bg-ds-ink-100 focus-visible:ring-ds-focus inline-flex h-11 cursor-pointer items-center gap-1 px-2 font-semibold transition-colors duration-150 first-letter:uppercase focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none"
          >
            {visao === "dias" ? nomeDoMes(mesVisivel) : anoEscolhido}
            <ChevronDown
              aria-hidden="true"
              className="text-ds-ink-600 size-4"
            />
          </button>
        )}

        <button
          type="button"
          onClick={() =>
            visao === "dias"
              ? trocarMes(1)
              : visao === "meses"
                ? setAnoEscolhido(ano => ano + 1)
                : setPaginaDeAnos(inicio => inicio + ANOS_POR_PAGINA)
          }
          disabled={
            (visao === "meses" && anoForaDoLimite(anoEscolhido + 1)) ||
            (visao === "anos" &&
              anoForaDoLimite(paginaDeAnos + ANOS_POR_PAGINA))
          }
          aria-label={
            visao === "dias"
              ? "Próximo mês"
              : visao === "meses"
                ? "Próximo ano"
                : "Próximos anos"
          }
          className={CLASSE_DA_SETA}
        >
          <ChevronRight aria-hidden="true" className="size-5" />
        </button>
      </div>

      {visao === "anos" && (
        <div
          role="group"
          aria-label="Escolher o ano"
          className="grid grid-cols-3 gap-2 py-2"
        >
          {Array.from(
            { length: ANOS_POR_PAGINA },
            (_, indice) => paginaDeAnos + indice
          ).map(ano => {
            const atual = ano === mesVisivel.getFullYear();
            return (
              <button
                key={ano}
                type="button"
                disabled={anoForaDoLimite(ano)}
                aria-pressed={atual}
                onClick={() => escolherAno(ano)}
                className={cn(
                  CLASSE_DA_OPCAO,
                  atual
                    ? "bg-ds-brand-600 text-ds-on-solid font-semibold"
                    : "text-ds-ink-900 hover:bg-ds-ink-100 disabled:hover:bg-transparent",
                  ano === hoje.getFullYear() &&
                    !atual &&
                    "text-ds-brand-700 font-semibold"
                )}
              >
                {ano}
              </button>
            );
          })}
        </div>
      )}

      {visao === "meses" && (
        <div
          role="group"
          aria-label={`Escolher o mês de ${anoEscolhido}`}
          className="grid grid-cols-3 gap-2 py-2"
        >
          {MESES_DO_ANO.map((nome, mes) => {
            const atual =
              anoEscolhido === mesVisivel.getFullYear() &&
              mes === mesVisivel.getMonth();
            return (
              <button
                key={nome}
                type="button"
                disabled={mesForaDoLimite(anoEscolhido, mes)}
                aria-pressed={atual}
                aria-label={new Date(anoEscolhido, mes, 1).toLocaleDateString(
                  "pt-BR",
                  { month: "long", year: "numeric" }
                )}
                onClick={() => escolherMes(mes)}
                className={cn(
                  CLASSE_DA_OPCAO,
                  atual
                    ? "bg-ds-brand-600 text-ds-on-solid font-semibold"
                    : "text-ds-ink-900 hover:bg-ds-ink-100 disabled:hover:bg-transparent"
                )}
              >
                {nome}
              </button>
            );
          })}
        </div>
      )}

      {visao === "dias" && (
        <table
          ref={gradeRef}
          role="grid"
          aria-busy={disabled || undefined}
          aria-label={`${label}, ${nomeDoMes(mesVisivel)}`}
          onKeyDown={aoTeclar}
          className="w-full border-collapse"
        >
          <thead>
            <tr>
              {DIAS_DA_SEMANA.map(dia => (
                <th
                  key={dia.longo}
                  scope="col"
                  abbr={dia.longo}
                  className="font-ds-sans text-ds-caption text-ds-ink-600 pb-1 font-normal"
                >
                  {dia.curto}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {semanasDoMes(mesVisivel).map(semana => (
              <tr key={semana[0].toISOString()}>
                {semana.map((dia, diaDaSemana) => {
                  const doMes = isSameMonth(dia, mesVisivel);
                  const selecionado = !!value && isSameDay(dia, value);
                  const ehHoje = isSameDay(dia, hoje);
                  const noIntervalo = dentroDoIntervalo(dia);
                  const indisponivel = bloqueado(dia);
                  const dica = getDayTooltip?.(dia) ?? null;

                  const botao = (
                    <button
                      type="button"
                      tabIndex={isSameDay(dia, focado) ? 0 : -1}
                      // `aria-disabled` em vez de `disabled`: dia bloqueado
                      // precisa continuar recebendo foco, senão as setas do
                      // teclado levam o foco para um elemento que não o aceita,
                      // ele cai no <body> e a navegação morre no meio da grade.
                      aria-disabled={indisponivel || undefined}
                      aria-current={ehHoje ? "date" : undefined}
                      aria-label={dia.toLocaleDateString("pt-BR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                      onClick={() => {
                        if (indisponivel) return;
                        setFocado(dia);
                        onSelect(dia);
                      }}
                      className={cn(
                        "ds-numeric rounded-ds-md font-ds-sans text-ds-small relative inline-flex size-11 items-center justify-center",
                        "ease-ds-out cursor-pointer transition-colors duration-150",
                        "focus-visible:ring-ds-focus focus-visible:ring-2 focus-visible:outline-none",
                        // Sem `disabled`, o cursor de bloqueado e a opacidade
                        // vêm do atributo ARIA.
                        "aria-disabled:cursor-not-allowed aria-disabled:opacity-40",
                        "motion-reduce:transition-none",
                        // Mês vizinho se distingue pelo PESO, não por uma cor
                        // apagada: o número do dia é informação e continua
                        // clicável, então não pode ficar abaixo de 4,5:1 —
                        // `ds-ink-400` dá 2,1:1.
                        !doMes && "text-ds-ink-600 font-normal",
                        doMes && "text-ds-ink-900",
                        ehHoje &&
                          !selecionado &&
                          "text-ds-brand-700 font-semibold",
                        selecionado &&
                          "bg-ds-brand-600 text-ds-on-solid font-semibold",
                        !selecionado &&
                          !indisponivel &&
                          (noIntervalo
                            ? "hover:bg-ds-brand-100"
                            : "hover:bg-ds-ink-100")
                      )}
                    >
                      {dia.getDate()}
                    </button>
                  );
                  // Onde a faixa termina NESTA linha, e o que existe nas linhas
                  // vizinhas. É com isso que cada canto decide se arredonda: no
                  // meio de um período de várias semanas, canto arredondado abre
                  // um degrau entre uma linha e outra.
                  const comecaNaLinha =
                    noIntervalo &&
                    (diaDaSemana === 0 || !dentroDoIntervalo(addDays(dia, -1)));
                  const terminaNaLinha =
                    noIntervalo &&
                    (diaDaSemana === 6 || !dentroDoIntervalo(addDays(dia, 1)));
                  const temAcima =
                    noIntervalo && dentroDoIntervalo(addDays(dia, -7));
                  const temAbaixo =
                    noIntervalo && dentroDoIntervalo(addDays(dia, 7));

                  return (
                    // O papel de célula é do <td>: posto no <button>, virava
                    // gridcell dentro de gridcell e apagava o papel de botão —
                    // o leitor de tela deixava de anunciar que dá para acionar.
                    <td
                      key={dia.toISOString()}
                      role="gridcell"
                      aria-selected={selecionado}
                      className="relative p-0 text-center"
                    >
                      {/* A faixa é uma camada própria atrás do dia, e não o fundo
                        da célula: a célula é mais larga que o alvo de 44px, e
                        pintá-la inteira fazia o laranja claro sobrar ao lado do
                        primeiro dia. Ela começa e termina exatamente na borda do
                        quadrado de 44px — o dia inteiro dentro da faixa, nada
                        além dele. */}
                      {noIntervalo ? (
                        <span
                          aria-hidden="true"
                          className={cn(
                            "bg-ds-brand-50 pointer-events-none absolute inset-y-0",
                            comecaNaLinha
                              ? "left-[calc(50%_-_1.375rem)]"
                              : "left-0",
                            terminaNaLinha
                              ? "right-[calc(50%_-_1.375rem)]"
                              : "right-0",
                            // Cada canto olha a linha vizinha: só arredonda o que
                            // é ponta de verdade do período.
                            comecaNaLinha && !temAcima && "rounded-tl-ds-md",
                            comecaNaLinha && !temAbaixo && "rounded-bl-ds-md",
                            terminaNaLinha && !temAcima && "rounded-tr-ds-md",
                            terminaNaLinha && !temAbaixo && "rounded-br-ds-md"
                          )}
                        />
                      ) : null}

                      {dica ? (
                        <Tooltip content={dica} className="whitespace-normal">
                          {botao}
                        </Tooltip>
                      ) : (
                        botao
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
